import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ChevronDown,
  Cpu,
  Dna,
  FlaskConical,
  Globe2,
  Landmark,
  Loader2,
  Rocket,
  Stethoscope,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { topicCategories } from "@/content/topics";
import { trackEvent } from "@/lib/analytics";
import { api, getApiErrorDetail } from "@/lib/api";

const CATEGORY_ICONS = { Stethoscope, Landmark, Dna, Rocket, Globe2, FlaskConical, Cpu };

/**
 * The front door to topic quizzes: type a subject, or tap a category and pick from
 * its hand-reviewed topics. Both land on /learn/<slug>.
 */
export default function TopicPicker({ className = "", autoFocus = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [query, setQuery] = useState("");
  const [openCategory, setOpenCategory] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const resolve = async (rawQuery) => {
    setPending(true);
    setError(null);
    setSuggestions([]);
    trackEvent("topic_search_submitted", { query_length: rawQuery.length });
    try {
      const { data } = await api.resolveTopic(rawQuery);
      trackEvent("topic_selected", { slug: data.slug, entry: "typed" });
      navigate(`/learn/${data.slug}`);
    } catch (err) {
      const detail = getApiErrorDetail(err);
      const options = detail?.details?.options || detail?.details?.suggestions || [];
      setSuggestions(options);
      setError(detail?.message || t("topics.searchFailed"));
      trackEvent("topic_not_found", { query: rawQuery, code: detail?.code });
    } finally {
      setPending(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed && !pending) resolve(trimmed);
  };

  const toggleCategory = (categoryId) => {
    const next = openCategory === categoryId ? null : categoryId;
    setOpenCategory(next);
    if (next) trackEvent("topic_category_opened", { category: next });
  };

  return (
    <div className={className}>
      <form onSubmit={onSubmit} className="relative">
        <label htmlFor="topic-query" className="sr-only">
          {t("topics.searchLabel")}
        </label>
        <Input
          id="topic-query"
          value={query}
          autoFocus={autoFocus}
          maxLength={200}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("topics.searchPlaceholder")}
          aria-describedby={error ? "topic-query-error" : undefined}
          className="h-16 rounded-3xl border-border/70 bg-card pr-16 pl-6 font-body text-base shadow-warm placeholder:text-muted-foreground/70 focus-visible:ring-terracotta/60 sm:text-lg"
        />
        <Button
          type="submit"
          disabled={pending || !query.trim()}
          aria-label={t("topics.searchSubmit")}
          className="absolute top-1/2 right-3 h-11 w-11 -translate-y-1/2 rounded-2xl bg-terracotta p-0 text-white btn-press hover:bg-terracotta-hover disabled:opacity-40"
        >
          {pending ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          )}
        </Button>
      </form>

      {error ? (
        <div
          id="topic-query-error"
          role="status"
          className="mt-3 rounded-2xl border border-border/70 bg-card px-5 py-4 text-sm"
        >
          <p className="font-body text-foreground">{error}</p>
          {suggestions.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {suggestions.slice(0, 5).map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => {
                    setQuery(suggestion);
                    resolve(suggestion);
                  }}
                  className="rounded-full border border-terracotta/40 px-3 py-1.5 font-body text-sm font-semibold text-terracotta transition-colors hover:bg-terracotta/10 focus-visible:ring-2 focus-visible:ring-terracotta/60 focus-visible:outline-none"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="mt-6 font-body text-sm font-semibold tracking-wide text-muted-foreground uppercase">
        {t("topics.orPickOne")}
      </p>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {topicCategories.map((category) => {
          const Icon = CATEGORY_ICONS[category.icon];
          const isOpen = openCategory === category.id;
          return (
            <button
              key={category.id}
              type="button"
              onClick={() => toggleCategory(category.id)}
              aria-expanded={isOpen}
              aria-controls={`topic-panel-${category.id}`}
              className={`card-hover flex w-full items-center gap-3 rounded-3xl border p-4 text-left transition-colors focus-visible:ring-2 focus-visible:ring-terracotta/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none ${
                isOpen
                  ? "border-terracotta/60 bg-terracotta/5"
                  : "border-border/70 bg-card shadow-warm"
              }`}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-terracotta/10 text-terracotta">
                <Icon className="h-5 w-5" strokeWidth={2.25} aria-hidden="true" />
              </span>
              <span className="font-heading truncate text-base font-bold text-foreground">
                {category.name}
              </span>
              <ChevronDown
                aria-hidden="true"
                className={`ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
          );
        })}
      </div>

      {/* Expands in place — no navigation until a topic is chosen. */}
      <AnimatePresence initial={false}>
        {topicCategories
          .filter((category) => category.id === openCategory)
          .map((category) => (
            <motion.div
              key={category.id}
              id={`topic-panel-${category.id}`}
              initial={reduceMotion ? false : { opacity: 0, height: 0 }}
              animate={reduceMotion ? { opacity: 1 } : { opacity: 1, height: "auto" }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.24, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {category.topics.map((topic) => (
                  <li key={topic.slug}>
                    <Link
                      to={`/learn/${topic.slug}`}
                      onClick={() =>
                        trackEvent("topic_selected", {
                          slug: topic.slug,
                          category: category.id,
                          entry: "tapped",
                        })
                      }
                      className="group flex items-center justify-between gap-3 rounded-2xl border border-border/70 bg-card px-5 py-4 transition-colors hover:border-terracotta/50 focus-visible:ring-2 focus-visible:ring-terracotta/60 focus-visible:outline-none"
                    >
                      <span className="font-heading text-sm font-bold text-foreground">
                        {topic.name}
                      </span>
                      <ArrowRight
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 text-terracotta transition-transform group-hover:translate-x-1"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
      </AnimatePresence>
    </div>
  );
}
